<template>
  <div class="row">
    <SwappingSquaresSpinner v-if="this.$store.getters.isLoading"
      :animation-duration="1000"
      :size="90"
      :color="this.$store.getters.config.palette.primary"
      style="margin: auto; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);"
    />
    <div v-if="!this.$store.getters.isLoading" class="flex xs12">
      <va-card :title="$t('clients.cardTitle')">
        <template slot="actions">
          <va-button :disabled="filteredList.length === 0" icon="fa fa-file-text-o" @click="generateCSV" />
          <va-button icon="fa fa-plus" @click.prevent="createClient" />
        </template>
        <div class="va-card__body" v-if="clients.length < 1">
          <va-notification v-if="clients.length < 1" color="info">
            <va-badge color="info">
              {{ $t('badges.info') }}
            </va-badge>
            {{ $t('clients.cardMessage') }}
          </va-notification>
        </div>
      </va-card>
    </div>

    <div v-if="!this.$store.getters.isLoading && clients.length > 0" class="flex xs12">
      <va-card stripe="primary" style="overflow: inherit;">
        <div class="row">
          <div class="flex md2 xs12">
            <va-input
              v-model="filter.plate"
              type="text"
              :label="$t('clients.search')"
              :disabled="clients.length === 0"
            />
          </div>
          <div class="flex md2 xs12">
            <va-select
              :label="$t('clients.selectStatus')"
              v-model="filter.status"
              keyBy="id"
              textBy="description"
              :options="statusOptions"
              @change="updateQueryParam"
            />
          </div>
          <div class="flex md3 xs12">
            <v-date-picker
              class="date-picker"
              color="purple"
              is-range
              v-model="dateRange"
              :value="null"
              :rows="2"
              :min-date="minimumDate.toISOString()"
              :max-date="maximumDate.toISOString()"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="date-picker__container">
                  <div class="va-input__container__content-wrapper">
                    <label>{{ $t('clients.labelFrom') }}</label>
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                    />
                  </div>
                  <svg
                    class="w-4 h-4 mx-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                  <div class="va-input__container__content-wrapper">
                    <label>{{ $t('clients.labelTo') }}</label>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                    />
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          <div class="flex md3 xs12">
            <va-select
              :label="$t('clients.selectLocation')"
              v-model="filter.location"
              keyBy="id"
              textBy="description"
              @input="onLocationChange"
              :options="locationOptions"
            />
          </div>
          <div class="flex md2 xs12">
            <va-select
              :label="$t('clients.selectEmployee')"
              v-model="user"
              keyBy="userId"
              textBy="name"
              :options="userOptions"
            />
          </div>
        </div>
        <va-list v-if="clients.length > 0" fit class="mb-2">
          <va-item v-if="filteredList.length === 0">
            <va-item-section>
              {{ $t('clients.noResults') }}
            </va-item-section>
          </va-item>
        </va-list>
      </va-card>
      <div class="cards-container row d-flex wrap align--start client-cards">
        <div class="flex xs12 sm6 lg4 xl3" v-for="(client, index) in filteredList" v-bind:key="client.clientId">
          <va-card
            :image="getClientImage(client.images)"
            :key="'item' + client.clientId"
          >
            <div class="va-card__header">
              <div class="va-card__header-title" style="color: rgb(113, 186, 255);">{{ (client.plate || 'n/a') }}</div>
              <div class="va-card__header-actions">
                <va-button-group>
                  <va-button v-if="client.clientReceipt" @click="downloadFileXHR(client.clientReceipt, 'receipt-' + Date.now() + '.pdf' )" flat small color="gray" :title="$t('clients.downloadReceipt')" icon-right="fa fa-ticket"></va-button>
                  <va-button v-if="client.clientInvoice" @click="downloadFileXHR(client.clientInvoice, 'invoice-' + Date.now() + '.pdf')" flat small color="gray" :title="$t('clients.downloadInvoice')" icon-right="fa fa-file-text"></va-button>
                </va-button-group>
                <KebabMenu>
                  <div slot="buttons">
                    <va-button-group>
                      <va-button flat small color="gray" @click="editModal(client, index)" :title="$t('clients.edit')" icon-right="fa fa-edit"></va-button>
                      <va-button flat small color="gray" :disabled="removeDisabled" @click="showPromptModal(client.clientId, index)" :title="$t('clients.delete')" icon-right="fa fa-trash"></va-button>
                    </va-button-group>
                  </div>
                </KebabMenu>
              </div>
            </div>
            <p class="ticket-number">{{ $t('clients.ticket') }}: <span>#{{ client.ticket }}</span> <va-badge :color="getStatusColor(client.status)">{{getStatusText(client.status)}}</va-badge><a v-if="client.mobile" href="tel:client.mobile"><span>{{ client.mobile }}</span></a></p>
            <p v-if="client.event.name">{{ $t('clients.location') }}: <span class="client-location" @click="toLocation(client.event.eventId)">{{ client.event.name }}</span></p>
            <p>{{ $t('clients.spot') }}: <span><strong>{{ client.spotIdentifier }}</strong> <va-icon :name='"fa fa-car"'></va-icon></span></p>
            <p v-if="client.carMake || client.carModel || client.carColor">{{ $t('clients.car') }}: <span>{{ (client.carMake || '') + ' ' + (client.carModel || '') + ' ' + (client.carColor || '') }}</span></p>
            <p v-if="!client.isWithoutPrice">{{ $t('clients.charge') }}: <span>{{ client.charge }} <va-icon :name='"fa fa-euro"'></va-icon></span></p>
            <p v-if="client.isWithoutPrice">{{ $t('clients.charge') }}: <span>{{ $t('clients.free') }}</span></p>
            <p v-if="client.period">{{ $t('clients.period') }}: <span>{{ client.period }} <va-icon :name='"fa fa-clock-o"'></va-icon></span></p>
            <p v-if="client.checkIn">{{ $t('clients.checkIn') }}: <span>{{ givenOnTime(client.checkIn) }}</span></p>
            <p v-if="client.checkOut">{{ $t('clients.checkOut') }}: <span>{{ givenOnTime(client.checkOut) }}</span></p>
          </va-card>
        </div>
      </div>
    </div>
    <ClientCreateModal @closemodal="closeClient" :open-modal="openCreateModal"/>
    <va-modal
      v-model="openEditModal"
      :title="$t('clients.editModal.title')"
      :hideDefaultActions="true"
      :noOutsideDismiss="true"
      :noEscDismiss="true"
    >
      <SwappingSquaresSpinner v-if="modalLoader"
        :animation-duration="1000"
        :size="90"
        :color="this.$store.getters.config.palette.primary"
        style="margin: 30px auto;"
      />
      <form v-if="!modalLoader">
        <div class="row">
          <va-input
            v-model="editClient.name"
            type="text"
            :label="$t('clients.editModal.labelName')"
            class="flex xs12"
          />
          <va-input
            v-model="editClient.surname"
            type="text"
            :label="$t('clients.editModal.labelSurname')"
            class="flex xs12"
          />
          <va-input
            v-model="editClient.email"
            type="email"
            :label="$t('clients.editModal.labelEmail')"
            class="flex xs12"
          />

          <va-input
            v-model="editClient.mobile"
            type="text"
            :label="$t('clients.editModal.labelMobile')"
            class="flex xs12"
          />

          <va-input
            v-model="editClient.plate"
            type="text"
            :label="$t('clients.editModal.labelPlate')"
            class="flex xs12"
          />

          <va-input
            v-model="editClient.carColor"
            type="text"
            :label="$t('clients.editModal.labelColor')"
            class="flex xs12"
          />

          <va-input
            v-model="editClient.carModel"
            type="text"
            :label="$t('clients.editModal.labelModel')"
            class="flex xs12"
          />
        </div>
        <div class="d-flex justify--center mt-3">
          <va-button color="gray" @click.prevent="disableModal()" class="my-0 ">{{ $t('clients.editModal.btnClose') }}</va-button>
          <va-button @click.prevent="oneditsubmit()" class="my-0">{{ $t('clients.editModal.btnUpdate') }}</va-button>
        </div>
      </form>
    </va-modal>
    <va-modal
      v-model="showRemoveModal"
      size="small"
      :title="$t('clients.deleteModal.title')"
      :message="$t('clients.deleteModal.message')"
      :hideDefaultActions="true"
      :noOutsideDismiss="true"
      :noEscDismiss="true"
    >
      <div class="d-flex justify--center mt-3">
        <va-button color="gray" @click="showRemoveModal=false" class="my-0 ">{{ $t('clients.deleteModal.btnNo') }}</va-button>
        <va-button type="submit" @click="removeClient(clientIdInPrompt, indexInPrompt)" class="my-0">{{ $t('clients.deleteModal.btnYes') }}</va-button>
      </div>
    </va-modal>
  </div>
</template>

<script>
import { SwappingSquaresSpinner } from 'epic-spinners'
import ClientCreateModal from '../modals/ClientCreateModal'
import errorHandlerMixin from '@/mixin/errorHandlerMixin'
import KebabMenu from '@/components/menus/KebabMenu.vue'
import 'v-calendar/src/styles/base.css'
import { stringify } from 'csv-stringify'

export default {
  name: 'Clients',
  components: { SwappingSquaresSpinner, ClientCreateModal, KebabMenu },
  mixins: [errorHandlerMixin],
  data () {
    return {
      openEditModal: false,
      openCreateModal: false,
      removeDisabled: false,
      modalLoader: false,
      showAdvanced: false,
      locations: [],
      editClient: {
        index: '',
        clientId: '',
        email: '',
        name: '',
        surname: '',
        mobile: '',
        carModel: '',
        carColor: '',
        plate: '',
        ticket: '',
        isTicketAutoGenerated: false,
        event: {},
      },
      ticketErrors: [],
      locationErrors: [],
      showRemoveModal: false,
      indexInPrompt: null,
      clientIdInPrompt: null,
      statusOptions: [
        {
          id: 1,
          value: 'taken',
          description: 'Parked',
        },
        {
          id: 2,
          value: 'request',
          description: 'Requested',
        },
        {
          id: 3,
          value: 'onthego',
          description: 'On the go',
        },
        {
          id: 4,
          value: 'awaiting',
          description: 'Awaiting',
        },
        {
          id: 5,
          value: 'completed',
          description: 'Completed',
        },
      ],
      locationOptions: [],
      locationSelectModel: '',
      filter: {
        plate: '',
        status: {
          id: null,
          value: '',
          description: '',
        },
        location: {
          id: 0,
          description: '',
        },
      },
      dateRange: {
        start: '',
        end: '',
      },
      minDate: new Date('2021-01-01'),
      maxDate: new Date(),
      userOptions: [],
      user: '',
    }
  },
  methods: {
    createClient () {
      this.openCreateModal = true
    },
    closeClient (state) {
      this.openCreateModal = state
    },
    showPromptModal (clientId, index) {
      this.clientIdInPrompt = clientId
      this.indexInPrompt = index
      this.showRemoveModal = true
    },
    async editModal (client, index) {
      this.editClient.index = index
      this.editClient.clientId = client.clientId
      this.editClient.email = client.email
      this.editClient.name = client.name
      this.editClient.surname = client.surname
      this.editClient.mobile = client.mobile
      this.editClient.carModel = client.carModel
      this.editClient.carColor = client.carColor
      this.editClient.plate = client.plate
      this.editClient.ticket = client.ticket
      this.editClient.isTicketAutoGenerated = client.isTicketAutoGenerated
      this.openEditModal = true
    },
    disableModal () {
      this.editClient.index = ''
      this.editClient.clientId = ''
      this.editClient.email = ''
      this.editClient.name = ''
      this.editClient.surname = ''
      this.editClient.mobile = ''
      this.editClient.carModel = ''
      this.editClient.carColor = ''
      this.editClient.plate = ''
      this.editClient.ticket = ''
      this.editClient.isTicketAutoGenerated = false
      this.editClient.event = {}
      this.ticketErrors = []
      this.locationErrors = []
      this.openCreateModal = false
      this.openEditModal = false
      this.modalLoader = false
      this.removeDisabled = false
      this.showAdvanced = false
    },
    onsubmit () {
      this.locationErrors = this.editClient.event ? [] : [this.$t('clients.requiredLocation')]
      if (!this.editClient.isTicketAutoGenerated) {
        this.ticketErrors = this.editClient.ticket ? [] : [this.$t('clients.requiredTicket')]
      }
      if (!this.formClientSubmitReady) {
        return
      }
      this.modalLoader = true
      this.$store.dispatch('clientCreate', {
        email: this.editClient.email,
        name: this.editClient.name,
        surname: this.editClient.surname,
        mobile: this.editClient.mobile,
        carModel: this.editClient.carModel,
        carColor: this.editClient.carColor,
        plate: this.editClient.plate,
        ticket: this.editClient.ticket,
        isTicketAutoGenerated: this.editClient.isTicketAutoGenerated,
        eventId: this.editClient.event.eventId,
      }).then((resp) => this.afterSubmit(resp.data.message))
        .catch(error => {
          this.errorHandler(error.response)
          this.modalLoader = false
          this.removeDisabled = false
        })
    },
    oneditsubmit () {
      this.modalLoader = true
      const payload = {
        message: {
          email: this.editClient.email,
          name: this.editClient.name,
          surname: this.editClient.surname,
          mobile: this.editClient.mobile,
          carModel: this.editClient.carModel,
          carColor: this.editClient.carColor,
          plate: this.editClient.plate,
        },
        clientId: this.editClient.clientId,
      }
      this.$store.dispatch('clientUpdate', payload)
        .then((resp) => this.afterEditSubmit(resp.data.message))
        .catch(error => {
          this.errorHandler(error.response)
          this.modalLoader = false
          this.removeDisabled = false
        })
    },
    removeClient (clientId, index) {
      this.removeDisabled = true
      this.$store.dispatch('clientsDelete', clientId).then(() => {
        // this.clients.splice(index, 1)
        this.$store.commit('remove_client', clientId)
      }).then(() => {
        this.sendRequestStatus(clientId, '', 'dashboard', 'removeclient')
        this.showRemoveModal = false
      }).then(() => this.disableModal())
        .catch(error => {
          this.errorHandler(error.response)
          this.modalLoader = false
          this.removeDisabled = false
          this.showRemoveModal = false
        })
    },
    async sendRequestStatus (identifier, status, scope, messageScope) {
      // await this.$socket.invoke('ProgressUpdate', identifier, status, scope, this.$store.getters.localUser.email, messageScope, null, this.$store.getters.namespace)
    },
    afterSubmit (client) {
      // this.clients.splice(this.clients.length, -1, client)
      this.$store.commit('new_client', client)
      this.disableModal()
    },
    afterEditSubmit (client) {
      // this.clients.splice(index, 1, client)
      this.$store.commit('update_client', client)
      this.disableModal()
    },
    getStatusColor (status) {
      if (status === 'taken') {
        return 'info'
      } else if (status === 'payment') {
        return 'info'
      } else if (status === 'request') {
        return 'warning'
      } else if (status === 'onthego') {
        return 'dark'
      } else if (status === 'awaiting') {
        return 'danger'
      } else if (status === 'completed') {
        return 'success'
      }
      return 'danger'
    },
    getStatusText (status) {
      if (status === 'taken') {
        return this.$t('clients.statusParked')
      } else if (status === 'payment') {
        return this.$t('clients.statusPaymentCompleted')
      } else if (status === 'request') {
        return this.$t('clients.statusCarRequested')
      } else if (status === 'onthego') {
        return this.$t('clients.statusOnTheGo')
      } else if (status === 'awaiting') {
        return this.$t('clients.statusAwaitingClient')
      } else if (status === 'completed') {
        return this.$t('clients.statusCompleted')
      }
      return status
    },
    async getTheLocations () {
      await this.$store.dispatch('getLocations')
        .then(() => {
          this.initLocations()
        })
        .then(() => {
          this.getLocationOptions()
        })
        .catch(error => {
          this.errorHandler(error.response)
          this.modalLoader = false
          this.removeDisabled = false
        })
    },
    async initLocations () {
      this.locations.splice(0, this.locations.length, ...this.$store.getters.locations)
    },
    getClientImage (images) {
      if (images.length && images[0].mediaLink) {
        return images[0].mediaLink
      }
      return require('@/assets/images/plate-placeholder.jpg')
    },
    givenOnTime (timestamp) {
      const moment = this.$moment(timestamp)
        .tz(this.$store.getters.theTimezone.zoneName)
      if (moment.isDST()) {
        return moment.add(1, 'hours').format('MMMM Do YYYY, h:mm:ss a')
      }
      return moment.format('MMMM Do YYYY, h:mm:ss a')
    },
    downloadFileXHR (filePath, fileName) {
      const xhh = new XMLHttpRequest()
      xhh.open('get', filePath)
      xhh.responseType = 'blob'
      xhh.onreadystatechange = function () {
        if (xhh.readyState === 4 && xhh.status === 200) {
          const blob = new Blob([xhh.response])
          const csvUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = csvUrl
          link.download = fileName
          link.click()
        }
      }
      xhh.send()
    },
    async toLocation (locationId) {
      await this.$router.push({
        name: 'location',
        params: { locationId: locationId },
      })
    },
    getLocationOptions () {
      this.locationOptions = this.locations.map(location => {
        return {
          id: location.eventId,
          description: location.name,
        }
      })
    },
    updateQueryParam () {
      const queryParams = { ...this.$route.query }
      queryParams.plate = this.filter.plate || 'all'
      queryParams.status = this.filter.status.value || 'all'
      queryParams.location = this.filter.location.id ? this.filter.location.id.toString() : '0'
      if (this.dateRange.start instanceof Date && this.dateRange.end instanceof Date) {
        queryParams.from = this.dateRange.start.toISOString().slice(0, 10)
        queryParams.to = this.dateRange.end.toISOString().slice(0, 10)
      } else {
        queryParams.from = this.dateRange.start
        queryParams.to = this.dateRange.end
      }
      queryParams.user = this.user && this.user.userId ? this.user.userId.toString() : '0'
      // Check if queryParams is different from the current route's query and push only if different
      if (JSON.stringify(queryParams) !== JSON.stringify(this.$route.query)) {
        this.$router.push({ path: this.$route.fullPath, query: queryParams })
      }
    },
    async initQueryFilters () {
      const status = this.statusOptions.filter((item) => {
        return item.value === this.$route.query.status
      })
      const location = this.locationOptions.filter((item) => {
        return item.id === Number(this.$route.query.location)
      })
      this.filter = {
        plate: this.$route.query.plate && this.$route.query.plate !== 'all' ? this.$route.query.plate : '',
        status: this.$route.query.status && this.$route.query.status !== 'all' ? status[0] : '',
        location: this.$route.query.location && this.$route.query.location !== '0' ? location[0] : '',
      }
      if (this.$route.query.location && this.$route.query.location !== '0') {
        const response = await this.$store.dispatch('appUserLocationRequest', this.filter.location.id)
        this.userOptions = response.data.message
        const user = this.userOptions.filter((item) => {
          return item.userId === Number(this.$route.query.user)
        })
        if (this.$route.query.user && this.$route.query.user !== '0') {
          this.user = user[0]
        }
      }
      const today = new Date()
      today.setDate(today.getDate() - 30)
      const fromDate = today.toISOString().slice(0, 10)
      const toDate = new Date().toISOString().slice(0, 10)
      this.dateRange = {
        start: this.$route.query.from || fromDate,
        end: this.$route.query.to || toDate,
      }
    },
    isDateInRange (datetimeString, fromDateString, toDateString) {
      const datetime = new Date(datetimeString)
      const fromDate = new Date(fromDateString)
      const toDate = new Date(toDateString)
      const datetimeDate = new Date(datetime.getFullYear(), datetime.getMonth(), datetime.getDate())
      const fromDateDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
      const toDateDate = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
      return datetimeDate >= fromDateDate && datetimeDate <= toDateDate
    },
    updatePeriod () {
      this.clients.forEach((client) => {
        if (client.period && client.status !== 'completed') {
          const [hours, minutes] = client.period.split(':')
          // Total number of minutes
          const totalMinutes = parseInt(hours) * 60 + parseInt(minutes)
          const newTotalMinutes = totalMinutes + 1
          // New hours (after adding one minute)
          const newHours = Math.floor(newTotalMinutes / 60).toString().padStart(2, '0')
          // New minutes (after adding one minute), add 0 before if digit is less than 10
          const newMinutes = (newTotalMinutes % 60).toString().padStart(2, '0')
          const formattedPeriod = `${newHours}:${newMinutes}`
          this.$store.commit('update_client_period', { clientId: client.clientId, period: formattedPeriod })
        }
      })
    },
    async onLocationChange () {
      if (this.filter.location && this.filter.location.id) {
        const response = await this.$store.dispatch('appUserLocationRequest', this.filter.location.id)
        this.userOptions = response.data.message
      } else {
        this.userOptions = []
        this.user = ''
      }
    },
    createSlug (str) {
      return str
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '_')
        .replace(/^-+|_+$/g, '')
    },
    csvDateFormat (dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear().toString().slice(2)
      const month = (date.getMonth() + 1).toString().padStart(2, '0') // Get the month and pad with leading zero if necessary
      const day = date.getDate().toString().padStart(2, '0') // Get the day and pad with leading zero if necessary

      return year + month + day
    },
    async getUser (userId, locationId) {
      const response = await this.$store.dispatch('appUserLocationRequest', locationId)

      const usersInLocation = response.data.message
      return usersInLocation.filter((item) => {
        return item.userId === Number(userId)
      })
    },
    async generateCSV () {
      const employeeId = this.$route.query.user
      const employee = this.userOptions.find(employee => employee.userId === Number(employeeId))

      const filteredData = await Promise.all(
        this.filteredList.map(async (item) => {
          let employeeName = ''
          let employeeSurname = ''
          if (employee) {
            employeeName = employee.name
            employeeSurname = employee.surname
          } else {
            try {
              const employeeObj = await this.getUser(item.userId, item.event.eventId)
              employeeName = employeeObj[0] && employeeObj[0].name ? employeeObj[0].name : ''
              employeeSurname = employeeObj[0] && employeeObj[0].surname ? employeeObj[0].surname : ''
            } catch (error) {
              console.error('Error while getting user:', error)
            }
          }
          return {
            'Location Name': item.event.name,
            'Employee Name': employeeName,
            'Employee Surname': employeeSurname,
            'Report start date': this.$route.query.from,
            'Report end date': this.$route.query.to,
            Plate: item.plate ? item.plate : 'N/A',
            Mobile: item.mobile,
            'Spot location': item.spotIdentifier,
            Charge: item.charge,
            Period: item.period,
            'Check-in date': item.checkIn ? this.givenOnTime(item.checkIn) : '',
            'Check-out date': item.checkOut ? this.givenOnTime(item.checkOut) : '',
            'Ticket number': item.ticket,
          }
        }),
      )

      let filename = ''
      if (this.$route.query.status && this.$route.query.status !== 'all') {
        filename += `${this.$route.query.status}_`
      }
      if (this.$route.query.from && this.$route.query.to) {
        filename += `${this.csvDateFormat(this.$route.query.from)}_${this.csvDateFormat(this.$route.query.to)}`
      }
      if (Number(this.$route.query.location)) {
        const locationId = this.$route.query.location
        const location = this.locationOptions.find(location => location.id === Number(locationId))
        filename += `_${this.createSlug(location.description)}`
      }
      if (employee) {
        filename += `_${employee.name.toLowerCase()}`
      }
      if (this.$route.query.plate && this.$route.query.plate !== 'all') {
        filename += `_${this.$route.query.plate.toLowerCase()}`
      }

      stringify(filteredData, { header: true }, (err, output) => {
        if (err) {
          console.error('Error while generating CSV', err)
          return
        }

        const link = document.createElement('a')
        link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(output)
        link.download = filename + '.csv'
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
      })
    },
  },
  computed: {
    clients () {
      return this.$store.getters.appClients
    },
    formClientSubmitReady () {
      return !(this.ticketErrors.length)
    },
    filteredList () {
      this.updateQueryParam()
      const value = (this.filter.plate.charAt(0).toLowerCase() + this.filter.plate.slice(1)).toLowerCase()
      const fromDate = new Date(this.dateRange.start)
      const toDate = new Date(this.dateRange.end)
      return this.clients.filter((client) => {
        const clientCopy = { ...client }
        if (clientCopy.plate === null || clientCopy.plate === '') {
          clientCopy.plate = 'n/a'
        }
        if (clientCopy.mobile) {
          clientCopy.mobile = clientCopy.mobile.replace(/\s/g, '')
        }
        return (clientCopy.plate.toLowerCase().includes(value) || (clientCopy.mobile && clientCopy.mobile.toLowerCase().includes(value))) &&
          (!this.filter.status.value || clientCopy.status === this.filter.status.value) &&
          (!this.filter.location.id || clientCopy.event.eventId === this.filter.location.id) &&
          ((!this.dateRange.start && !this.dateRange.end) || this.isDateInRange(clientCopy.checkIn, fromDate, toDate)) &&
          (!this.user || clientCopy.userId === this.user.userId)
      })
    },
    minimumDate () {
      return this.minDate
    },
    maximumDate () {
      return this.maxDate
    },
  },
  mounted () {
    this.$store.commit('setLoading', true)
    this.$store.dispatch('clientsRequest')
      .then(() => {
        this.$store.commit('setLoading', false)
      })
      .catch(error => {
        this.errorHandler(error.response)
        this.modalLoader = false
        this.removeDisabled = false
        this.$store.commit('setLoading', false)
      })
    this.getTheLocations().then(() => {
      this.initQueryFilters()
    })
    setInterval(() => {
      this.updatePeriod()
    }, 60000)
  },
}
</script>

<style lang="scss">
  .client-cards {
    .va-card {
      &__header {
        display: flex;
        align-items: center;

        &-title {
          font-size: 1rem;
          color: rgb(108, 127, 238) !important;

          &:hover {
            cursor: pointer;
          }
        }

        &-actions {
          display: flex;
        }
      }

      &__body {
        overflow: visible;

        .va-button--small.va-button--with-right-icon {
          padding-right: 0.75rem;
        }

        p {
          font-size: 14px;
        }

        .client-location {
          color: rgb(113, 186, 255);

          &:hover {
            cursor: pointer;
          }
        }

        .ticket-number {
          display: flex;
          align-items: center;

          .va-badge {
            margin-left: 5px;
          }

          a {
            color: rgb(113, 186, 255);
          }
        }
      }
    }
  }

  @media screen and (max-width: 767px) {
    .client-cards {
      .va-card {
        &__body {
          .ticket-number {
            a {
              font-size: 12px;
            }
          }
        }
      }
    }
  }

  .markup-tables {
    .va-table {
      width: 100%;
    }

    .va-card {
      max-width: 1200px;
    }
  }

  .va-item {
    cursor: default !important;
  }

  .date-picker {
    &__container {
      display: flex;
      align-items: center;

      & > div {
        display: flex;
        position: relative;
        width: 100%;
        min-height: 2.375rem;
        border-style: solid;
        border-width: 0 0 thin 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        background-color: rgb(245, 248, 249);
        border-color: rgb(131, 150, 165);
      }
    }

    label {
      position: absolute;
      bottom: 1rem;
      left: 0.5rem;
      margin-bottom: 0.5rem;
      max-width: calc(100% - 0.25rem);
      font-size: 0.625rem;
      letter-spacing: 0.0375rem;
      line-height: 1.2;
      font-weight: bold;
      text-transform: uppercase;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transform-origin: top left;
      color: rgb(108, 127, 238);
      z-index: 1;
    }

    input {
      display: flex;
      position: relative;
      width: 100%;
      background-color: rgb(245, 248, 249);
      border: none;
    }

    svg {
      width: 2rem;
      height: 2rem;
    }
  }
</style>
